/**
 * React bindings of the checker UI (W2-12a checker-runtime): translation through `services.i18n`, the problems
 * service snapshot, the open store, the editor's active line and the user settings, each re-rendering only when
 * its source changes (useSyncExternalStore).
 */

import { useCallback, useMemo, useSyncExternalStore } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { Locale, MessageRef, Unsubscribe } from '../../core/util/types';
import type { MessageKey } from '../../i18n/types';
import type { DocStore } from '../../state/types';
import { asCheckerService } from './problemsService';
export { useEditorValue, useSettings as useSettingsValue } from '../../app/registry/react';

export interface CheckerText {
  readonly locale: Locale;
  readonly t: (key: MessageKey | string, params?: Readonly<Record<string, string | number>>) => string;
  readonly tr: (ref: MessageRef) => string;
}

/** Translation through `services.i18n`; a new object per locale so memoised children update. */
export function useCheckerText(services: Pick<AppServices, 'i18n'>): CheckerText {
  const { i18n } = services;
  const subscribe = useCallback((cb: () => void): Unsubscribe => i18n.subscribe(cb), [i18n]);
  const get = useCallback((): Locale => i18n.locale, [i18n]);
  const locale = useSyncExternalStore(subscribe, get, get);
  return useMemo(
    () => ({
      locale,
      t: (key, params) => i18n.t(key, params),
      tr: (ref) => i18n.tr(ref),
    }),
    [i18n, locale],
  );
}

/**
 * A counter that changes whenever the problems service reports a change (its own version when it is the checker's
 * service, so filter and status changes count too).
 */
export function useProblemsVersion(services: Pick<AppServices, 'problems'>): number {
  const problems = services.problems;
  const counter = useMemo(() => ({ n: 0 }), []);
  const subscribe = useCallback(
    (cb: () => void): Unsubscribe =>
      problems.subscribe(() => {
        counter.n++;
        cb();
      }),
    [problems, counter],
  );
  const get = useCallback((): number => asCheckerService(problems)?.version ?? counter.n, [problems, counter]);
  return useSyncExternalStore(subscribe, get, get);
}

/** The open store (null in the Library), following service swaps. */
export function useOpenStore(services: Pick<AppServices, 'store' | 'onChange'>): DocStore | null {
  const subscribe = useCallback((cb: () => void): Unsubscribe => services.onChange(cb), [services]);
  const get = useCallback((): DocStore | null => services.store, [services]);
  return useSyncExternalStore(subscribe, get, get);
}

/** A counter of document changes of the open store (ignore list, line names, tempo). */
export function useStoreVersion(store: DocStore | null): number {
  const counter = useMemo(() => ({ n: 0 }), []);
  const subscribe = useCallback(
    (cb: () => void): Unsubscribe => {
      if (store === null) return () => undefined;
      return store.subscribe((cs) => {
        if (cs.source === 'gesture') return;
        counter.n++;
        cb();
      });
    },
    [store, counter],
  );
  const get = useCallback((): number => counter.n, [counter]);
  return useSyncExternalStore(subscribe, get, get);
}
